import { nullIfEmpty, parseJsonObject, readInstant, readObject, readString } from './json';

/**
 * Plan-06 status vocabulary and the `GET /api/host/status` document - port of
 * `HostLinkStatus.cs`. The contract source is `contracts/host-link/v1/`.
 *
 * `unknown` is NOT a fifth status. It is this client's "I was handed something
 * I do not understand", and it exists so an unrecognised value can never be
 * optimistically rendered as `online` - the one mistake that would put a
 * waiting trainee back into a race with the person mid-training.
 */
export type HostPublicStatus = 'unknown' | 'offline' | 'online' | 'in_use' | 'error';

/**
 * The host's INTERNAL states, carried in the status document's `host.state`.
 * Never rendered directly, but `activating` is what tells a waiting client to
 * say "preparing the training" rather than "waiting for vision" (Plan-06 §10.3).
 */
export type HostState = 'unknown' | 'starting' | 'dormant' | 'joining' | 'standby' | 'active' | 'activating';

/** What a claim is holding the cell for (Plan-06 §9, §10.3). */
export type HostClaimKind = 'unknown' | 'wearer' | 'activation';

/**
 * The client's copy of `contracts/host-link/v1/constants.json`. Every value is
 * a number two components must agree on, so change it in the contract first -
 * this file follows, it never leads. Contract is FROZEN at v1.
 */
export const HostLinkContract = {
  /** Envelope version this client understands. A document with any other `v` is refused. */
  contractVersion: 1,
  /** §9.6 - poll cadence (seconds) while somebody is actually standing there watching. */
  statusPollFastSeconds: 5,
  /** §9.6 - backed-off cadence once the wait stops being an active one. */
  statusPollSlowSeconds: 15,
  /** §9.6 - "backing off to ~15 s after a couple of minutes". */
  statusPollBackoffAfterSeconds: 120,
  /** §8.2 step 5 - the wake+join budget (seconds); beyond it, say so out loud. */
  wakeJoinBudgetSeconds: 3,
} as const;

/** The wearer claim as the status document reports it (§9.2). */
export interface HostClaim {
  readonly kind: HostClaimKind;
  /** The display name the client sent, so a refusal can say WHO. Null for an activation claim. */
  readonly device: string | null;
  /** Free text under the badge - e.g. "applying training CNCFixtureChangeover". */
  readonly detail: string | null;
  /** Epoch ms, or null when absent/unparseable. */
  readonly grantedAt: number | null;
  readonly expiresAt: number | null;
}

/** D25 §10.3 - a training change queued and waiting for an idle moment. */
export interface HostPendingActivation {
  readonly trainingId: string | null;
  readonly activeTrainingId: string | null;
  readonly requestedAt: number | null;
}

/**
 * The four-state status document served by `GET /api/host/status` (D24,
 * contract `statusDocument`) - read-only, unauthenticated, and the ONLY thing
 * a refused client is allowed to poll while the server is busy. Polling
 * `POST /api/token` instead would claim the slot the instant it freed, which
 * is retrying by another name (§9.6 step 3).
 */
export interface HostStatusDocument {
  readonly cell: string;
  readonly status: HostPublicStatus;
  /** The one human-readable line under the badge. Always present in a valid document. */
  readonly detail: string;
  /** Closed-set error reason; empty unless `status` is `error`. */
  readonly reason: string;
  /** When the CURRENT claim started (epoch ms) - renders as "started 12 minutes ago". Never an uptime. */
  readonly since: number | null;
  /** The raw `since` string, kept for logs when it does not parse. */
  readonly sinceRaw: string;
  readonly state: HostState;
  readonly hostStartedAt: number | null;
  readonly lastHeartbeatAt: number | null;
  readonly training: string;
  readonly detector: string;
  readonly claim: HostClaim | null;
  readonly pendingActivation: HostPendingActivation | null;
  readonly fetchedAt: number | null;
}

/** The server says a training may be started. The one condition that enables Join (§9.6 step 5). */
export function isHostAvailable(document: HostStatusDocument): boolean {
  return document.status === 'online';
}

/**
 * §10.3 - an activation is in progress or pending. A model load can take tens
 * of seconds, and silence that long reads as a hang, so a client seeing this
 * says "preparing the training" instead of "waiting for vision".
 */
export function isHostActivating(document: HostStatusDocument): boolean {
  return (
    document.state === 'activating' ||
    document.claim?.kind === 'activation' ||
    document.pendingActivation !== null
  );
}

/** The training being activated, when one is known. */
export function activatingTrainingId(document: HostStatusDocument): string {
  return document.pendingActivation?.trainingId ?? document.training;
}

function parsePublicStatus(value: string): HostPublicStatus {
  switch (value) {
    case 'offline':
    case 'online':
    case 'in_use':
    case 'error':
      return value;
    default:
      return 'unknown';
  }
}

function parseHostState(value: string): HostState {
  switch (value) {
    case 'starting':
    case 'dormant':
    case 'joining':
    case 'standby':
    case 'active':
    case 'activating':
      return value;
    default:
      return 'unknown';
  }
}

export function parseClaimKind(value: string): HostClaimKind {
  switch (value) {
    case 'wearer':
    case 'activation':
      return value;
    default:
      return 'unknown';
  }
}

/**
 * Parse a `GET /api/host/status` body. Null for anything this client cannot
 * vouch for - a different contract version, an unrecognised status word, or
 * unparseable JSON. Tolerant about everything the client does not need,
 * deliberately strict about the two things it does.
 */
export function parseHostStatusDocument(json: string): HostStatusDocument | null {
  const root = parseJsonObject(json);
  if (!root) return null;

  // A version we do not understand is refused rather than guessed at: the
  // whole point of the envelope's `v` is that two sides that disagree stop
  // instead of improvising (contract README).
  if (root['v'] !== HostLinkContract.contractVersion) return null;

  const status = parsePublicStatus(readString(root, 'status'));
  if (status === 'unknown') return null;

  const host = readObject(root, 'host');
  const claimRoot = readObject(root, 'claim');
  const pendingRoot = readObject(root, 'pendingActivation');

  const claim: HostClaim | null = claimRoot
    ? {
        kind: parseClaimKind(readString(claimRoot, 'kind')),
        device: nullIfEmpty(readString(claimRoot, 'device')),
        detail: nullIfEmpty(readString(claimRoot, 'detail')),
        grantedAt: readInstant(claimRoot, 'grantedAt'),
        expiresAt: readInstant(claimRoot, 'expiresAt'),
      }
    : null;

  // `since` is the document's own answer to "started when", but a claim that
  // carries grantedAt and a document that does not is still answerable - and
  // that sentence is the whole point of the waiting screen.
  const since = readInstant(root, 'since') ?? claim?.grantedAt ?? null;

  return {
    cell: readString(root, 'cell'),
    status,
    detail: readString(root, 'detail'),
    reason: readString(root, 'reason'),
    since,
    sinceRaw: readString(root, 'since'),
    state: host ? parseHostState(readString(host, 'state')) : 'unknown',
    hostStartedAt: host ? readInstant(host, 'startedAt') : null,
    lastHeartbeatAt: host ? readInstant(host, 'lastHeartbeatAt') : null,
    training: host ? readString(host, 'training') : '',
    detector: host ? readString(host, 'detector') : '',
    claim,
    pendingActivation: pendingRoot
      ? {
          trainingId: nullIfEmpty(readString(pendingRoot, 'trainingId')),
          activeTrainingId: nullIfEmpty(readString(pendingRoot, 'activeTrainingId')),
          requestedAt: readInstant(pendingRoot, 'requestedAt'),
        }
      : null,
    fetchedAt: readInstant(root, 'fetchedAt'),
  };
}

/**
 * `GET /api/host/status` for a config endpoint. The Pages project serves every
 * route on one origin (settled in the contract README), so the config URL's
 * origin is the whole answer. Null when the config URL is not absolute http(s).
 */
export function resolveHostStatusEndpoint(configUrl: string, cell?: string | null): string | null {
  let uri: URL;
  try {
    uri = new URL(configUrl);
  } catch {
    return null;
  }
  if (uri.protocol !== 'http:' && uri.protocol !== 'https:') return null;
  const endpoint = `${uri.protocol}//${uri.host}/api/host/status`;
  return cell ? `${endpoint}?cell=${encodeURIComponent(cell)}` : endpoint;
}
